import {
  createFileUploadUrl,
  submitJob,
  getJob,
  terminateJob,
  listMovementLogs,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/http';
import {
  JobStatus,
  type DataMovementJob,
  type SubmitJobRequest,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';

const UPLOAD_BASE = 'https://import-upload.test';
const UPLOAD_PATH = '/file';
const FILE_ID = 'file-1';
const JOB_ID = 'job-1';

export type ImportJobOutcome = 'completed' | 'partial' | 'failed';

/**
 * Test mocks for the data-hub Hub Job import lifecycle, mirroring
 * `exportServiceMocks`. Auto-included in the test container's httpClient
 * scenarios so every import test drives a fake server. Behavior is controllable
 * per test via `setOutcome` / `setInProgressPolls`, and the uploaded (aligned)
 * CSV + the submitted job request are captured for assertions.
 */
export function hubJobImportMocks(
  whenRequest: typeof import('@wix/http-client-testkit/client').whenRequest,
  when: typeof import('@wix/http-client-testkit/client').when,
) {
  const state = {
    outcome: 'completed' as ImportJobOutcome,
    succeeded: 0,
    failed: 0,
    /** new items created (subset of `succeeded`), for the summary step */
    created: 0,
    /** existing items updated (subset of `succeeded`), for the summary step */
    updated: 0,
    /**
     * When true, the terminal job omits `itemsSuccessfulInfo` entirely — the
     * created/updated breakdown some jobs (and older servers) don't report, in
     * which case only the total `itemsSuccessful` is available.
     */
    noBreakdown: false,
    /** number of IN_PROGRESS polls before the job goes terminal */
    inProgressPolls: 0,
    /** csvRows indices reported as failed via movement logs */
    failedRowIndices: [] as number[],
    /** the raw body PUT to the signed upload URL (the aligned CSV File) */
    uploadedBody: undefined as unknown,
    /** the SubmitJobRequest payload */
    submittedRequest: undefined as SubmitJobRequest | undefined,
    submittedJobId: undefined as string | undefined,
    terminatedJobIds: [] as string[],
  };

  const pollCounts = new Map<string, number>();

  const terminalStatus = (): JobStatus =>
    state.outcome === 'failed'
      ? JobStatus.FAILED
      : state.outcome === 'partial'
      ? JobStatus.PARTIALLY_SUCCESSFUL
      : JobStatus.COMPLETED;

  const total = () => state.succeeded + state.failed;

  const terminalJob = (id: string): DataMovementJob => ({
    id,
    status: terminalStatus(),
    progress: {
      current: total(),
      total: total(),
      itemsSuccessful: state.succeeded,
      itemsRejected: state.failed,
      ...(!state.noBreakdown && {
        itemsSuccessfulInfo: {
          itemsCreated: state.created,
          itemsUpdated: state.updated,
        },
      }),
    },
    ...(state.outcome === 'failed' && {
      error: { code: 'import_failed', description: 'Import job failed' },
    }),
  });

  return {
    state,

    /**
     * Set the job's terminal outcome and its counts. `created`/`updated` are the
     * split of `succeeded` reported to the summary step; when omitted they
     * default so `created` covers all successes (older jobs report neither).
     */
    setOutcome(
      outcome: ImportJobOutcome,
      counts: {
        succeeded?: number;
        failed?: number;
        created?: number;
        updated?: number;
        /** Omit the created/updated breakdown from the job (see `noBreakdown`). */
        noBreakdown?: boolean;
      } = {},
    ) {
      state.outcome = outcome;
      state.succeeded = counts.succeeded ?? 0;
      state.failed = counts.failed ?? 0;
      state.updated = counts.updated ?? 0;
      state.created = counts.created ?? state.succeeded - state.updated;
      state.noBreakdown = counts.noBreakdown ?? false;
    },

    /** Make the job report IN_PROGRESS for `n` polls before going terminal. */
    setInProgressPolls(n: number) {
      state.inProgressPolls = n;
    },

    /** Row indices `listMovementLogs` reports as failed (sourceItemId). */
    setFailedRowIndices(indices: number[]) {
      state.failedRowIndices = indices;
    },

    /** The aligned CSV uploaded to the signed URL, as text. */
    async getUploadedCsv(): Promise<string> {
      const body = state.uploadedBody;
      if (body == null) {
        return '';
      }
      if (typeof body === 'string') {
        return body;
      }
      const blob = body as Blob;
      // jsdom's File implements neither text() nor arrayBuffer() here, so fall
      // back to FileReader.
      if (typeof blob.text === 'function') {
        return blob.text();
      }
      if (typeof blob.arrayBuffer === 'function') {
        return new TextDecoder().decode(await blob.arrayBuffer());
      }
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });
    },

    reset() {
      state.outcome = 'completed';
      state.succeeded = 0;
      state.failed = 0;
      state.created = 0;
      state.updated = 0;
      state.noBreakdown = false;
      state.inProgressPolls = 0;
      state.failedRowIndices = [];
      state.uploadedBody = undefined;
      state.submittedRequest = undefined;
      state.submittedJobId = undefined;
      state.terminatedJobIds = [];
      pollCounts.clear();
    },

    mocks: [
      whenRequest(createFileUploadUrl)
        .reply(200, {
          uploadUrl: `${UPLOAD_BASE}${UPLOAD_PATH}`,
          fileId: FILE_ID,
        })
        .persist(),

      when(UPLOAD_BASE)
        .put(UPLOAD_PATH)
        .reply(200, (data: unknown) => {
          state.uploadedBody = data;
          return {};
        })
        .persist(),

      whenRequest(submitJob)
        .reply(200, (payload: SubmitJobRequest) => {
          state.submittedRequest = payload;
          state.submittedJobId = JOB_ID;
          pollCounts.set(JOB_ID, 0);
          return {
            job: {
              id: JOB_ID,
              status: JobStatus.INITIALIZING,
              progress: { current: 0, total: total() },
            },
          };
        })
        .persist(),

      whenRequest(getJob)
        .reply(200, (payload: { jobId: string }) => {
          const seen = pollCounts.get(payload.jobId) ?? 0;
          pollCounts.set(payload.jobId, seen + 1);

          if (seen < state.inProgressPolls) {
            // No `total` here so ImportState keeps its initial CSV-row total;
            // `current` is 0-based so the first poll reports nothing processed.
            return {
              job: {
                id: payload.jobId,
                status: JobStatus.IN_PROGRESS,
                progress: {
                  current: seen,
                  itemsSuccessful: seen,
                  itemsRejected: 0,
                },
              },
            };
          }

          return { job: terminalJob(payload.jobId) };
        })
        .persist(),

      whenRequest(terminateJob)
        .reply(200, (payload: { jobId: string }) => {
          state.terminatedJobIds.push(payload.jobId);
          return {};
        })
        .persist(),

      whenRequest(listMovementLogs)
        .reply(200, () => ({
          // data-hub formats a CSV item's sourceItemId as "row N", where N is
          // the 1-based file line (header is line 1) — so csvRows[i] is "row i+2".
          logs: state.failedRowIndices.map((index) => ({
            sourceItemId: `row ${index + 2}`,
            failure: { code: 'row_failed', description: 'Invalid row' },
          })),
          pagingMetadata: { hasNext: false },
        }))
        .persist(),
    ] as import('@wix/http-client-testkit').AnyScenario[],
  };
}
